/**
 * Canvas Mode Types
 *
 * Defines the operational modes for the automation flow canvas.
 * The mode determines all canvas behaviors including editing capabilities,
 * toolbar visibility, and contact enrichment strategies.
 *
 * This is the single source of truth for canvas behavior configuration,
 * replacing the previous fragmented approach of passing isReadOnly through
 * multiple layers.
 *
 * @example
 * ```typescript
 * // In a page component:
 * <AutomationFlowCanvas mode="editing" ... />
 *
 * // Mode determines all behavior:
 * const config = useCanvasMode('editing');
 * // config.isReadOnly === false
 * // config.showNodePalette === true
 * ```
 */

/**
 * Operational mode of the automation flow canvas.
 *
 * - `readonly`: No editing, used for visualization/testing (admin testing page)
 * - `testing`: Viewing with contact testing enabled (web testing page)
 * - `preview`: Read-only but selectable nodes, used for published version preview in editor
 * - `editing`: Full editing mode with all features (flow editor page)
 */
export type CanvasMode = 'readonly' | 'testing' | 'preview' | 'editing';

/**
 * Contact enrichment strategy for visualizing test contacts in the flow.
 *
 * - `api-driven`: Uses real contact step data from the API (for admin readonly)
 * - `simulated`: Uses frontend simulation for preview (for testing and editing)
 */
export type ContactEnrichmentMode = 'api-driven' | 'simulated';

/**
 * Configuration derived from a CanvasMode.
 *
 * This interface defines all the behaviors that change based on the canvas mode.
 * Components should use the useCanvasMode hook to derive this configuration
 * rather than checking mode directly.
 *
 * @example
 * ```typescript
 * const config = useCanvasMode(mode);
 *
 * // Use config to conditionally render features:
 * {config.showToolbar && <CanvasControls />}
 * {config.showNodePalette && <NodePalette />}
 *
 * // Pass readonly state to ReactFlow:
 * <ReactFlow
 *   nodesDraggable={config.nodesDraggable}
 *   nodesConnectable={config.nodesConnectable}
 * />
 * ```
 */
export interface CanvasModeConfig {
	/** The canvas mode this config was derived from */
	mode: CanvasMode;

	/** Whether nodes are in read-only mode (no editing) */
	isReadOnly: boolean;

	/** Whether nodes can be dragged to new positions */
	nodesDraggable: boolean;

	/** Whether new edges can be created between nodes */
	nodesConnectable: boolean;

	/** Whether to show the CanvasControls toolbar with zoom and view controls */
	showToolbar: boolean;

	/** Whether to show the floating add buttons at chain termini */
	showChainTerminusButtons: boolean;

	/** Whether to show the keyboard shortcuts help panel */
	showKeyboardShortcuts: boolean;

	/** Whether to show the node palette for adding new steps */
	showNodePalette: boolean;

	/** Which contact enrichment strategy to use for visualization */
	contactEnrichmentMode: ContactEnrichmentMode;

	/** Whether to allow selecting nodes */
	elementsSelectable: boolean;

	/** Whether to allow deleting nodes */
	deleteKeyCode: string | null;

	/** Whether to enable selection box (marquee) for multi-selection */
	selectionOnDrag: boolean;

	/** Key code for additive selection (e.g., 'Shift' for shift+click) */
	multiSelectionKeyCode: string | null;

	/** Whether to show context menu on right-click */
	showContextMenu: boolean;

	/** Mouse buttons that trigger pan (false = no pan on drag, array = specific buttons only) */
	panOnDrag: boolean | number[];
}

/**
 * Default configurations for each canvas mode.
 *
 * These are the baseline configurations. The useCanvasMode hook
 * returns these values based on the provided mode.
 */
export const CANVAS_MODE_CONFIGS: Record<CanvasMode, CanvasModeConfig> = {
	readonly: {
		mode: 'readonly',
		isReadOnly: true,
		nodesDraggable: false,
		nodesConnectable: false,
		showToolbar: false,
		showChainTerminusButtons: false,
		showKeyboardShortcuts: false,
		showNodePalette: false,
		contactEnrichmentMode: 'api-driven',
		elementsSelectable: false,
		deleteKeyCode: null,
		selectionOnDrag: false,
		multiSelectionKeyCode: null,
		showContextMenu: false,
		panOnDrag: true,
	},
	preview: {
		mode: 'preview',
		isReadOnly: true,
		nodesDraggable: false,
		nodesConnectable: false,
		showToolbar: true,
		showChainTerminusButtons: false,
		showKeyboardShortcuts: false,
		showNodePalette: false,
		contactEnrichmentMode: 'simulated',
		elementsSelectable: true,
		deleteKeyCode: null,
		selectionOnDrag: false,
		multiSelectionKeyCode: null,
		showContextMenu: false,
		panOnDrag: true,
	},
	testing: {
		mode: 'testing',
		isReadOnly: false,
		nodesDraggable: false,
		nodesConnectable: false,
		showToolbar: true,
		showChainTerminusButtons: false,
		showKeyboardShortcuts: true,
		showNodePalette: false,
		contactEnrichmentMode: 'simulated',
		elementsSelectable: true,
		deleteKeyCode: null,
		selectionOnDrag: false,
		multiSelectionKeyCode: null,
		showContextMenu: false,
		panOnDrag: true,
	},
	editing: {
		mode: 'editing',
		isReadOnly: false,
		nodesDraggable: true,
		nodesConnectable: true,
		showToolbar: true,
		showChainTerminusButtons: true,
		showKeyboardShortcuts: true,
		showNodePalette: true,
		contactEnrichmentMode: 'simulated',
		elementsSelectable: true,
		deleteKeyCode: 'Delete',
		selectionOnDrag: true,
		multiSelectionKeyCode: 'Shift',
		showContextMenu: true,
		panOnDrag: [1, 2],
	},
};
